import map from 'lodash/map';
import includes from 'lodash/includes';
import some from 'lodash/some';

// Components
import HdpContentWrapper from 'app/shared/modules/hdp/HdpContentWrapper';
import CarouselV3 from 'app/shared/modules/CarouselV3';
import { ListingProvider } from 'app/shared/modules/listing-cards/ListingCardProvider';
import ListingCardV3 from 'app/shared/modules/listing-cards/ListingCardV3';
import ImpressionTracking from 'app/shared/modules/ImpressionTracking';
import {
  emitTrackClicksIntoPropertyEvent,
  emitTrackImpressionForPropertyCardEvent,
  type PropertyCardTriggerLocation,
} from 'app/shared/models/Clickstream/ListingCardClickstreamEvents';

// Utils
import { shouldShowTotalMonthlyPriceBadge } from 'app/shared/utils/totalMonthlyPriceUtils';

// Types
import type { ListingDetails } from '@/srcLegacy/types/listingDetails.type';
import type {
  SaveHomeTriggerLocation,
  SaveHomeTriggerObject,
} from 'app/shared/models/Clickstream/HdpClickstreamEvents';

interface FeaturedListingCollectionProps {
  header?: string;
  isTreatingListingsNotViewed?: boolean;
  listings: Array<ListingDetails>;
  onListingClick?: () => void;
  screenWidthForDesktopSRPFilter?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  propertyCardTriggerLocation: PropertyCardTriggerLocation;
  triggerLocation?: SaveHomeTriggerLocation;
  triggerObject?: SaveHomeTriggerObject;
}

interface CarouselConfig {
  itemsPerPage: number;
}

const getCarouselConfig = (screenWidthForDesktopSRPFilter?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl'): CarouselConfig => {
  if (screenWidthForDesktopSRPFilter === 'xl') {
    return { itemsPerPage: 2 };
  }
  return { itemsPerPage: 1 };
};

const FeaturedListingCollection = ({
  header,
  isTreatingListingsNotViewed,
  listings,
  onListingClick,
  screenWidthForDesktopSRPFilter,
  propertyCardTriggerLocation,
  triggerLocation,
  triggerObject,
}: FeaturedListingCollectionProps) => {
  if (listings.length === 0) {
    return null;
  }

  const isMobile = screenWidthForDesktopSRPFilter === 'sm';
  const carouselConfig = getCarouselConfig(screenWidthForDesktopSRPFilter);
  const isShowingTotalPrice = some(listings, (listing) => shouldShowTotalMonthlyPriceBadge(listing));

  return (
    <HdpContentWrapper active fullContent>
      <CarouselV3
        data-testid="carousel"
        freeScrollMode={isMobile}
        header={header}
        headerTag="h2"
        isHdpFeaturedListings={true}
        itemsPerPage={carouselConfig.itemsPerPage}
        showPaginationDots
        style={{ paddingLeft: '16px', paddingRight: '16px' }}
      >
        {map(listings, (listing) => {
          if (listing) {
            const listingViewed = isTreatingListingsNotViewed
              ? false
              : includes(listing?.userItemTypes, 'viewed') || includes(listing?.userItemTypes, 'favorite');

            return (
              <ListingProvider
                listing={listing}
                triggerLocation={triggerLocation as SaveHomeTriggerLocation}
                triggerObject={triggerObject as SaveHomeTriggerObject}
                key={`${listing.aliasEncoded}-container`}
              >
                <ListingCardV3
                  highPriority={true}
                  listingViewed={listingViewed}
                  onClick={() => {
                    emitTrackClicksIntoPropertyEvent({
                      listing,
                      triggerLocation: propertyCardTriggerLocation,
                    });
                    onListingClick?.();
                  }}
                  shouldLazyLoadListingCardPhotos={true}
                  isCarouselCard={true}
                  forceUniformHeight={isShowingTotalPrice}
                />
                <ImpressionTracking
                  analyticsCallback={() =>
                    emitTrackImpressionForPropertyCardEvent({
                      listing,
                      triggerLocation: propertyCardTriggerLocation,
                    })
                  }
                />
              </ListingProvider>
            );
          }
        })}
      </CarouselV3>
    </HdpContentWrapper>
  );
};

export default FeaturedListingCollection;
